import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import ButtonComponent from "../../../atoms/button";
import CountryListDropDown from "../../countryListDropDown";
import { shallowEqual, useSelector } from "react-redux";
import { desktopIndiaConfig } from "../../../../constants/mockData/mockData";
import isEmpty from "lodash.isempty";
import Drawer from "../../drawer";
import { DELIVER_IN_60_COUNTRIES } from "../../../../constants/common/common";
import { debounce } from "../../../../utils/common";
import Portal from "../../Portal";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";

/**
 * Component for selecting a country from a dropdown list.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {Function} props.setShowCountryList - Function to set the visibility of the country list.
 * @param {boolean} props.showCountryList - Boolean to control the visibility of the country list.
 * @param {Function} props.isFromPDP - Boolean to control the UI.
 * @param {function} onCountrySelection - Callback function trigger when country selection.
 */
const CountryDropdown = ({
  setShowCountryList,
  isShowCountryList,
  isFromPDP = false,
  isSeparateFieldsDesign = false,
  onCountrySelection,
  isError,
  isMobile,
  isDisabled = false,
  isFromOutsideDrawer = false,
}) => {
  const isJoinedFieldLayout = isFromPDP && !isSeparateFieldsDesign;
  const { visitedCountry, onFocusChangeFromTextfield } = useSelector(
    (state) => ({
      visitedCountry: state.locationLock.visitedCountry,
      onFocusChangeFromTextfield: state.locationLock.onFocusChangeFromTextfield,
    }),
    shallowEqual,
  );
  const [selectedCountry, setSelectedCountry] = useState(
    isEmpty(visitedCountry) ? desktopIndiaConfig.defaultCountry : visitedCountry,
  );
  /**
   * Toggles the visibility of the country list.
   * Sets the state to show or hide the country list.
   * @function onToggleCountryList
   */
  const onToggleCountryList = debounce(() => {
    setShowCountryList(!isShowCountryList);
  }, 50);

  /**
   * Handles the selection of a country.
   * Sets the selected country, hides the country list, and dispatches actions to update the state.
   * @function onCountryClickHandler
   * @param {Object} item - The selected country object.
   */
  const onCountryClickHandler = (item) => {
    if (!isFromPDP || isMobile) {
      setSelectedCountry(item);
    }
    onCountrySelection(item);
    onToggleCountryList();
  };

  useEffect(() => {
    if (!isEmpty(visitedCountry)) {
      setSelectedCountry(visitedCountry);
    }
  }, [visitedCountry]);

  /**
   * Handles the closing of the country list drawer.
   * Toggles the visibility of the country list.
   * @function onHandleDrawerClose
   */
  const onHandleDrawerClose = () => {
    setShowCountryList(!isShowCountryList);
  };
  return (
    <>
      <ButtonComponent
        classes={`${isDisabled ? "cursor-not-allowed bg-grey-100" : ""} ${isJoinedFieldLayout ? "rounded-e-none border-e-0" : "countryDropdown"} ${isJoinedFieldLayout && onFocusChangeFromTextfield ? "border-black-100" : "border-gray-300"} ${isError ? "border-red" : ""} h-[44px] md:h-auto`}
        id="search-country-button"
        subIcon={
          isShowCountryList ? (
            <CaretUpIcon size={20} weight="bold" className="text-black-100" />
          ) : (
            <CaretDownIcon size={20} weight="bold" className="text-black-100" />
          )
        }
        subIconClass="ml-0"
        label={isJoinedFieldLayout ? selectedCountry?.countryId : selectedCountry?.countryName}
        size="customForPDP"
        buttonVariant="countryDropdown"
        onClick={onToggleCountryList}
        icon={selectedCountry?.webLogo}
        isDisabled={isDisabled}
        customIconStyleWidth={isMobile || !isJoinedFieldLayout ? "24px" : "100%"}
      />
      {/* Ex=> ToDo: this isFromPDP is should be page dependent */}
      <div
        className={`${isJoinedFieldLayout && "absolute top-[55px] z-2 w-full bg-white-900"} ${!isMobile && isShowCountryList ? "rounded-8 border-1 border-greySoft p-12" : ""}`}
      >
        {isMobile ? (
          <Portal
            rootId={isFromPDP || isFromOutsideDrawer ? "bottom_drawer" : "mobileLocationLock"}
            renderOnDidMount={true}
          >
            <Drawer
              position="bottom"
              isOpen={isShowCountryList}
              onDrawerClose={onHandleDrawerClose}
              styles={`px-16 max-h-[80vh] pb-[40px]`}
              isPreventUnlockBody={!isFromPDP}
              isCleanUp={isFromPDP}
              isHideCloseBtn={isFromPDP}
              isBackButtonEnabled={isFromPDP}
              headingText={isFromPDP ? DELIVER_IN_60_COUNTRIES : ""}
              headingClass="[&&]:px-0"
            >
              <CountryListDropDown
                title={isFromPDP ? "" : DELIVER_IN_60_COUNTRIES}
                onCountryClick={onCountryClickHandler}
                selectedCountry={selectedCountry}
                isMobile={isMobile}
              />
            </Drawer>
          </Portal>
        ) : (
          isShowCountryList && (
            <CountryListDropDown
              onCountryClick={onCountryClickHandler}
              selectedCountry={selectedCountry}
              isFromPDP={isFromPDP}
              isMobile={isMobile}
            />
          )
        )}
      </div>
    </>
  );
};

export default CountryDropdown;

CountryDropdown.propTypes = {
  setShowCountryList: PropTypes.func.isRequired,
  isShowCountryList: PropTypes.bool.isRequired,
  isFromPDP: PropTypes.bool,
  isSeparateFieldsDesign: PropTypes.bool,
  onCountrySelection: PropTypes.func,
  isError: PropTypes.bool,
  isMobile: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFromOutsideDrawer: PropTypes.bool,
};
